import { supabase } from "../supabase/SupabaseClient";
import { actual } from "./quien";

import {
  BUCKET_HERRAMIENTAS,
  CALIDAD_JPEG,
  LADO_MAYOR,
  TOPE_FOTOS,
  avisoDeTope,
  cuantasEntran,
  esImagen,
  extensionDe,
  medidaAchicada,
  nombreUnico,
  rutaDeFoto,
} from "../shared/herramientasFotos";

export * from "../shared/herramientasFotos";

/* Las fotos de una herramienta, contra la base (P-348).
 *
 * ── OPTATIVAS, Y AL COSTADO DEL DIBUJO ───────────────────────────────────────
 *
 * El 14/09 las fotos se sacaron porque eran obligatorias y reemplazaban al
 * dibujo: cada alta era una sesión de fotos. Ahora ninguna de las dos cosas:
 * una herramienta sin fotos está completa, y el tablero se sigue dibujando
 * con `tipo`. Las fotos se miran en la ficha de UNA herramienta.
 *
 * ── LA FILA GUARDA LA RUTA, NO LA URL ────────────────────────────────────────
 *
 * El archivo va al bucket `herramientas` (público para leer) en
 * `fotos/<id>/<único>.<ext>`, y la fila de `taller_herramienta_foto` guarda
 * esa ruta. La URL se arma al leer. Es lo mismo que hace el bot, que sube por
 * WhatsApp a la misma tabla.
 *
 * ── SE QUITA, NO SE BORRA ────────────────────────────────────────────────────
 *
 * Quitar le pone `quitada_en`; la fila y el archivo quedan como constancia.
 * Quién y cuándo lo sella la base, que no se puede falsear desde acá.
 */

const TABLA = "taller_herramienta_foto";
const COLUMNAS = "id, herramienta_id, ruta, mime, bytes, ancho, alto, origen, " +
  "subido_por_nombre, creado_en";

/* ¿El error es porque la base todavía no tiene la tabla? La migración la
   aplica Supabase al mergear y el Hub se publica por su lado: si el Hub llega
   primero, la ficha tiene que seguir andando sin fotos, no caerse. */
export const faltaTabla = (error) =>
  /taller_herramienta_foto/.test(String(error?.message || error || "")) &&
  /does not exist|Could not find the table|schema cache|42P01|PGRST205/i
    .test(String(error?.message || error || ""));

/* Los errores de la base, en castellano de mostrador. */
export function motivoFotos(mensaje) {
  const m = String(mensaje || "");
  if (/row-level security|permission denied|42501|Unauthorized|403/i.test(m)) {
    return "No tenés permiso para cargar fotos de herramientas.";
  }
  if (/taller_herramienta_foto/.test(m) &&
      /does not exist|Could not find the table|schema cache|42P01|PGRST205/i.test(m)) {
    return "Las fotos todavía no se pueden cargar desde acá.";
  }
  if (/payload too large|413|maximum allowed size/i.test(m)) {
    return "La foto es demasiado pesada.";
  }
  return "No se pudo subir. Probá de nuevo.";
}

/* La URL pública de una ruta del bucket. */
export function urlDeFoto(ruta) {
  if (!ruta) return null;
  const { data } = supabase.storage.from(BUCKET_HERRAMIENTAS).getPublicUrl(ruta);
  return data?.publicUrl || null;
}

const conUrl = (f) => ({ ...f, url: urlDeFoto(f.ruta) });

/* ¿Ya se pueden cargar fotos? Lo pregunta el ALTA, que no tiene herramienta
   que leer: sin esto ofrecería juntar fotos que al guardar no van a subir.

   ES UN GET Y NO UN HEAD: a una tabla que no existe, un HEAD le contesta 404
   sin cuerpo y el cliente lo da por bueno. Si falla por otra cosa (la red),
   se sigue ofreciendo: la subida dirá lo suyo. */
export async function sePuedenCargar() {
  const { error } = await supabase.from(TABLA).select("id").limit(1);
  return !faltaTabla(error);
}

/* Las fotos VIVAS de una herramienta, de la más vieja a la más nueva. Una
   quitada no se muestra en ningún lado.

   «NO PUDE LEER» NO ES «NO TIENE FOTOS»: devuelve `{fotos, error}` y nunca
   una lista vacía a secas cuando falla. */
export async function fotosDe(herramientaId) {
  if (!herramientaId) return { fotos: [], error: "", faltaTabla: false };
  const { data, error } = await supabase
    .from(TABLA)
    .select(COLUMNAS)
    .eq("herramienta_id", herramientaId)
    .is("quitada_en", null)
    .order("creado_en", { ascending: true })
    .order("id", { ascending: true });
  if (error) {
    console.warn("[herramientas] no pude leer las fotos:", error.message);
    return { fotos: [], error: error.message, faltaTabla: faltaTabla(error) };
  }
  return { fotos: (data || []).map(conUrl), error: "", faltaTabla: false };
}

/* La PRIMERA foto viva de cada herramienta y cuántas tiene, para la miniatura
   de la tabla de administrar (lo que el aviso de P-348 le prometió a Nico: «la
   foto se ve en la lista de administrar»). Una sola consulta para toda la
   tabla. Si falla —o la tabla todavía no está—, la lista se ve igual, sin
   miniaturas: es un adorno, no un dato. */
export async function primerasFotos() {
  const { data, error } = await supabase
    .from(TABLA)
    .select("id, herramienta_id, ruta")
    .is("quitada_en", null)
    .order("creado_en", { ascending: true })
    .order("id", { ascending: true });
  if (error) return { porHerramienta: {}, error: error.message };
  const porHerramienta = {};
  for (const f of data || []) {
    const ya = porHerramienta[f.herramienta_id];
    if (ya) ya.cuantas += 1;
    else porHerramienta[f.herramienta_id] = { url: urlDeFoto(f.ruta), cuantas: 1 };
  }
  return { porHerramienta, error: "" };
}

/* ── Achicar en el navegador ──────────────────────────────────────────────────
 *
 * Una foto de teléfono son 4.000 px y 8 MB. Se dibuja en un canvas con el
 * lado mayor en 1.600 px y se guarda como JPEG al 82 %: unos 300 KB, y se
 * sigue reconociendo la herramienta. Si el navegador no la puede abrir (un
 * HEIC en una compu, por ejemplo) no se sube: nadie la podría mirar.
 */

async function abrirImagen(archivo) {
  if (typeof createImageBitmap === "function") {
    try {
      // `from-image`: respeta la rotación que el teléfono anota en la foto.
      return await createImageBitmap(archivo, { imageOrientation: "from-image" });
    } catch {
      /* Algunos navegadores no aceptan la opción: se prueba con <img>. */
    }
  }
  return new Promise((listo, falla) => {
    const url = URL.createObjectURL(archivo);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); listo(img); };
    img.onerror = () => { URL.revokeObjectURL(url); falla(new Error("no abre")); };
    img.src = url;
  });
}

const aJpeg = (canvas, calidad) => new Promise((listo, falla) => {
  canvas.toBlob(
    (blob) => (blob ? listo(blob) : falla(new Error("no se pudo achicar"))),
    "image/jpeg",
    calidad,
  );
});

/* Hasta este peso, un JPEG que ya tiene la medida no se vuelve a comprimir. */
const PESO_SIN_TOCAR = 1.5 * 1024 * 1024;

export async function achicarFoto(archivo, { lado = LADO_MAYOR, calidad = CALIDAD_JPEG } = {}) {
  const imagen = await abrirImagen(archivo);
  const ancho0 = imagen.width || imagen.naturalWidth || 0;
  const alto0 = imagen.height || imagen.naturalHeight || 0;
  const medida = medidaAchicada(ancho0, alto0, lado);
  const tipo = String(archivo.type || "").toLowerCase();

  /* Un JPEG que ya entra —en medidas y en peso— se sube tal cual: volver a
     comprimirlo sólo le saca calidad. */
  if (tipo === "image/jpeg" && medida.ancho === ancho0 && medida.alto === alto0 &&
      (archivo.size ?? 0) <= PESO_SIN_TOCAR) {
    imagen.close?.();
    return { blob: archivo, mime: "image/jpeg", ancho: ancho0, alto: alto0,
             bytes: archivo.size ?? null };
  }

  const canvas = document.createElement("canvas");
  canvas.width = medida.ancho;
  canvas.height = medida.alto;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no se pudo achicar");
  /* Fondo blanco: el JPEG no tiene transparencia y un PNG recortado saldría
     con el fondo negro. */
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, medida.ancho, medida.alto);
  ctx.drawImage(imagen, 0, 0, medida.ancho, medida.alto);
  imagen.close?.();
  const blob = await aJpeg(canvas, calidad);
  canvas.width = 0;
  canvas.height = 0;
  return { blob, mime: "image/jpeg", ancho: medida.ancho, alto: medida.alto,
           bytes: blob.size ?? null };
}

/* ── Subir ────────────────────────────────────────────────────────────────────
 *
 * Una por una: si una falla, las otras suben igual, y se dice cuántas no.
 * Primero el archivo y después la fila; si la fila no se puede anotar, se
 * borra el archivo para no dejarlo suelto en el bucket.
 *
 * EL TOPE SE CUENTA EN LA BASE y no con lo que muestra la pantalla: el bot u
 * otra pestaña pueden haber sumado fotos mientras tanto. Las que no entran no
 * se suben, y se avisa.
 *
 * `alProgreso(hechas, total)` para el «Subiendo 2 de 5…». `faltaTabla` en la
 * respuesta: la base todavía no tiene dónde anotarlas, y volver a probar no
 * sirve (la cuenta no lo nota: va con HEAD, ver `sePuedenCargar`).
 */
export async function subirFotos(herramientaId, archivos, { alProgreso } = {}) {
  const lista = Array.from(archivos || []).filter(Boolean);
  const vacio = { subidas: [], fallidas: [], sobran: 0, aviso: "", faltaTabla: false };
  if (!herramientaId) return { ...vacio, ok: false, error: "Falta la herramienta." };
  if (!lista.length) return { ...vacio, ok: true, error: "" };

  const { count, error: errCuenta } = await supabase
    .from(TABLA)
    .select("id", { count: "exact", head: true })
    .eq("herramienta_id", herramientaId)
    .is("quitada_en", null);
  if (errCuenta) {
    return { ...vacio, ok: false, error: motivoFotos(errCuenta.message),
             faltaTabla: faltaTabla(errCuenta) };
  }
  const { entran, sobran } = cuantasEntran(count || 0, lista.length);
  const yo = actual();
  const subidas = [];
  const fallidas = [];
  let sinTabla = false;

  const tanda = lista.slice(0, entran);
  /* Cuántas quedaron afuera porque, mientras se subía esta tanda, entró otra
     foto por otro lado (WhatsApp, otra pestaña) y se llegó al tope. Ver abajo. */
  let pasadas = 0;
  for (let i = 0; i < tanda.length; i += 1) {
    const archivo = tanda[i];
    alProgreso?.(i + 1, tanda.length);
    const nombre = archivo.name || `foto ${i + 1}`;
    if (pasadas) {
      pasadas += 1;
      continue;
    }
    if (!esImagen(archivo)) {
      fallidas.push({ nombre, error: "No es una foto." });
      continue;
    }
    let foto;
    try {
      foto = await achicarFoto(archivo);
    } catch {
      fallidas.push({ nombre, error: "No se pudo abrir la foto. Probá con otra." });
      continue;
    }
    const ruta = rutaDeFoto(herramientaId, extensionDe(foto.mime, archivo.name), nombreUnico());
    const bucket = supabase.storage.from(BUCKET_HERRAMIENTAS);
    const { error: errSubida } = await bucket.upload(ruta, foto.blob, {
      contentType: foto.mime, upsert: false,
    });
    if (errSubida) {
      fallidas.push({ nombre, error: motivoFotos(errSubida.message) });
      continue;
    }
    const { data, error } = await supabase
      .from(TABLA)
      .insert([{
        herramienta_id: herramientaId,
        ruta,
        mime: foto.mime,
        bytes: foto.bytes,
        ancho: foto.ancho,
        alto: foto.alto,
        origen: "hub",
        subido_por: yo?.mail || null,
        subido_por_nombre: yo?.nombre || null,
      }])
      .select(COLUMNAS)
      .single();
    if (error || !data) {
      sinTabla = sinTabla || faltaTabla(error);
      try { await bucket.remove([ruta]); } catch { /* queda suelto; no es grave */ }
      fallidas.push({ nombre, error: motivoFotos(error?.message) });
      continue;
    }
    /* EL TOPE SE VUELVE A MIRAR DESPUÉS DE ANOTAR. La cuenta de arriba es de
       antes de la tanda: si mientras tanto entró una por WhatsApp, esta sería
       la 13. Se cuentan las vivas hasta esta (por id, que crece) y, si se pasó,
       se quita la propia —la anterior ya estaba— y no se suben las que siguen.
       Es la misma regla que usa el bot del otro lado. */
    const { count: hastaEsta, error: errRecuento } = await supabase
      .from(TABLA)
      .select("id", { count: "exact", head: true })
      .eq("herramienta_id", herramientaId)
      .is("quitada_en", null)
      .lte("id", data.id);
    if (!errRecuento && (hastaEsta || 0) > TOPE_FOTOS) {
      const quitada = await quitarFoto(data.id);
      if (quitada.ok) {
        pasadas += 1;
        continue;
      }
      // Si no se pudo quitar, quedó viva: se cuenta como subida, que es lo que pasó.
    }
    subidas.push(conUrl(data));
  }

  const aviso = avisoDeTope(sobran + pasadas);
  if (!fallidas.length) {
    return { ok: true, error: "", subidas, fallidas, sobran: sobran + pasadas, aviso,
             faltaTabla: false };
  }
  const motivos = [...new Set(fallidas.map((f) => f.error))].join(" ");
  const cuantas = fallidas.length === 1
    ? "Una foto no se pudo subir"
    : `${fallidas.length} fotos no se pudieron subir`;
  return { ok: false, error: `${cuantas}: ${motivos}`, subidas, fallidas,
           sobran: sobran + pasadas, aviso, faltaTabla: sinTabla };
}

/* Quitar una foto: deja de verse en todos lados. La fila queda con quién y
   cuándo (lo sella la base). Sin la llave la base no da error —la RLS filtra
   la fila y el update no toca nada—, así que se pide la fila de vuelta y, si
   no vino, se dice. */
export async function quitarFoto(fotoId) {
  if (!fotoId) return { ok: false, error: "Falta la foto." };
  const yo = actual();
  const { data, error } = await supabase
    .from(TABLA)
    .update({ quitada_en: new Date().toISOString(), quitada_por: yo?.mail || null })
    .eq("id", fotoId)
    .is("quitada_en", null)
    .select("id");
  if (error) return { ok: false, error: motivoFotos(error.message) };
  if (!data || !data.length) {
    return { ok: false, error: "Esa foto ya no estaba (o no tenés permiso). Actualizá la pantalla." };
  }
  return { ok: true, error: "" };
}
